/**
 * SchematicMapBuilder — productor del mapa esquemático del municipio (INF-5a)
 *
 * Genera un SVG autocontenido (data-URL) con el contorno del término municipal
 * y los puntos de infraestructura coloreados por confianza, distinguiendo los
 * supramunicipales. Alimenta `ExportOptions.mapaEstatico` (consumidor INF-5b,
 * `HTMLReportExporter.generateMapaSection`), que lo renderiza en
 * `<section id="mapa"><img src="${dataUrl}"></section>` sin tocarse.
 *
 * Función PURA y determinista: sin DOM, sin red, sin Date ni random. Apta para
 * tests deterministas y para ejecutarse en la capa wizard (P4) antes de
 * `ExportService.exportAll`.
 *
 * Arco INFORME-HTML-PTEL · cierra el par consumidor→productor (patrón B1a→B1b).
 * Decisión de producto (20260622): esquema vectorial, sin teselas ni basemap.
 *
 * @module services/export/SchematicMapBuilder
 */

import proj4 from 'proj4';
import type { Polygon, MultiPolygon, Position } from 'geojson';
import type { ProcessedRecord } from '../../types/export.types';
import type { MunicipalBoundary } from '../../lib/spatialValidation';

// Registro idempotente EPSG:25830 / EPSG:4326 (mismo patrón que MunicipalBoundaryService)
if (!proj4.defs('EPSG:25830')) {
  proj4.defs('EPSG:25830', '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs');
}
if (!proj4.defs('EPSG:4326')) {
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
}

/** Flag de scoring (INF-3) que marca una infraestructura supramunicipal confirmada. */
export const SUPRAMUNICIPAL_FLAG = 'SUPRAMUNICIPAL_CONFIRMADO';

/** Subconjunto de `ProcessedRecord` que el productor necesita leer (desacopla tests). */
export type SchematicMapPoint = Pick<
  ProcessedRecord,
  'xUtm30' | 'yUtm30' | 'confianza' | 'scoreDetail'
>;

/** Opciones del mapa esquemático (todas con valor por defecto). */
export interface SchematicMapOptions {
  /** Ancho lógico del SVG en px (default 800). */
  width?: number;
  /** Alto lógico del SVG en px (default 600). */
  height?: number;
  /** Margen interno en px alrededor del encuadre (default 32). */
  padding?: number;
}

const DEFAULTS: Required<SchematicMapOptions> = { width: 800, height: 600, padding: 32 };

// Paleta por confianza — colores de dato (semáforo), sin glow ni box-shadow.
const COLOR_CONFIANZA: Record<string, string> = {
  alta: '#2e7d32',
  media: '#f9a825',
  baja: '#ef6c00',
  critica: '#c62828',
};
const COLOR_DEFAULT = '#5f6368';
const COLOR_BOUNDARY_STROKE = '#37474f';
const COLOR_BOUNDARY_FILL = '#eceff1';
const COLOR_BG = '#ffffff';
const COLOR_SUPRA_STROKE = '#263238';

interface XY { x: number; y: number; }
interface PlottedPoint extends XY { color: string; supra: boolean; }

/** Redondea a 2 decimales (determinismo del SVG emitido). */
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

function isSupra(p: SchematicMapPoint): boolean {
  return p.scoreDetail?.flags?.includes(SUPRAMUNICIPAL_FLAG) ?? false;
}

function isValidUtm(x: number, y: number): boolean {
  return Number.isFinite(x) && Number.isFinite(y) && !(x === 0 && y === 0);
}

/** Extrae los puntos dibujables (UTM30) descartando coordenadas inválidas. */
function extractPoints(points: ReadonlyArray<SchematicMapPoint>): PlottedPoint[] {
  const out: PlottedPoint[] = [];
  for (const p of points) {
    if (!isValidUtm(p.xUtm30, p.yUtm30)) continue;
    out.push({
      x: p.xUtm30,
      y: p.yUtm30,
      color: COLOR_CONFIANZA[p.confianza] ?? COLOR_DEFAULT,
      supra: isSupra(p),
    });
  }
  return out;
}

/** Proyecta los anillos exteriores del término (WGS84→UTM30). Devuelve anillos en metros. */
function projectBoundary(boundary: MunicipalBoundary | null): XY[][] {
  if (!boundary) return [];
  const geom = boundary.geometry;
  const polygons: Position[][][] =
    geom.type === 'MultiPolygon'
      ? (geom as MultiPolygon).coordinates
      : [(geom as Polygon).coordinates];
  const rings: XY[][] = [];
  for (const poly of polygons) {
    const exterior = poly[0]; // anillo exterior (los huecos se omiten en el esquema)
    if (!exterior || exterior.length < 3) continue;
    const ring: XY[] = [];
    for (const pos of exterior) {
      const [x, y] = proj4('EPSG:4326', 'EPSG:25830', [pos[0], pos[1]]);
      if (isValidUtm(x, y)) ring.push({ x, y });
    }
    if (ring.length >= 3) rings.push(ring);
  }
  return rings;
}

type BBox4 = [number, number, number, number]; // [minX, minY, maxX, maxY] en metros

/** BBox sobre la unión de todos los XY. Garantiza span>0 (guard de caso degenerado). */
function unionBBox(all: XY[]): BBox4 {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of all) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  // Caso degenerado (punto único o línea): expandir ±0,5 m para evitar escala ∞.
  if (maxX - minX < 1) { minX -= 0.5; maxX += 0.5; }
  if (maxY - minY < 1) { minY -= 0.5; maxY += 0.5; }
  return [minX, minY, maxX, maxY];
}

interface Projector { toSvg(p: XY): XY; }

/**
 * Proyector metros→SVG: preserva aspecto, centra el encuadre y voltea Y
 * (en SVG la Y crece hacia abajo; en UTM la Y/Norte crece hacia arriba).
 */
function makeProjector(bbox: BBox4, w: number, h: number, pad: number): Projector {
  const [minX, minY, maxX, maxY] = bbox;
  const dataW = maxX - minX;
  const dataH = maxY - minY;
  const usableW = w - 2 * pad;
  const usableH = h - 2 * pad;
  const scale = Math.min(usableW / dataW, usableH / dataH);
  const offsetX = (w - dataW * scale) / 2;
  const offsetY = (h - dataH * scale) / 2;
  return {
    toSvg(p: XY): XY {
      const px = offsetX + (p.x - minX) * scale;
      const py = h - (offsetY + (p.y - minY) * scale); // flip Y
      return { x: r2(px), y: r2(py) };
    },
  };
}

/** Anillos del término como `<path>` (relleno tenue + trazo, sin glow). */
function boundarySvg(rings: XY[][], proj: Projector): string {
  return rings
    .map((ring) => {
      const d =
        ring
          .map((p, i) => {
            const s = proj.toSvg(p);
            return `${i === 0 ? 'M' : 'L'}${s.x},${s.y}`;
          })
          .join(' ') + ' Z';
      return `<path d="${d}" fill="${COLOR_BOUNDARY_FILL}" fill-opacity="0.6" stroke="${COLOR_BOUNDARY_STROKE}" stroke-width="2" stroke-linejoin="round" />`;
    })
    .join('');
}

/** Un punto: círculo (intramunicipal) o rombo con trazo oscuro (supramunicipal). */
function pointSvg(p: PlottedPoint, proj: Projector): string {
  const s = proj.toSvg(p);
  if (p.supra) {
    const d =
      `M${s.x},${r2(s.y - 7)} L${r2(s.x + 7)},${s.y} ` +
      `L${s.x},${r2(s.y + 7)} L${r2(s.x - 7)},${s.y} Z`;
    return `<path d="${d}" fill="${p.color}" stroke="${COLOR_SUPRA_STROKE}" stroke-width="2" />`;
  }
  return `<circle cx="${s.x}" cy="${s.y}" r="6" fill="${p.color}" stroke="#ffffff" stroke-width="1.5" />`;
}

const LEGEND_ITEMS: ReadonlyArray<{ label: string; color: string; supra?: boolean }> = [
  { label: 'Alta', color: COLOR_CONFIANZA.alta },
  { label: 'Media', color: COLOR_CONFIANZA.media },
  { label: 'Baja', color: COLOR_CONFIANZA.baja },
  { label: 'Crítica', color: COLOR_CONFIANZA.critica },
  { label: 'Supramunicipal', color: COLOR_DEFAULT, supra: true },
];

/** Leyenda compacta (confianza + marcador supramunicipal). Fuente JetBrains Mono. */
function legendSvg(): string {
  const x = 12, y = 12, rowH = 20, padIn = 8, w = 168;
  const h = LEGEND_ITEMS.length * rowH + padIn * 2;
  let rows = '';
  LEGEND_ITEMS.forEach((it, i) => {
    const cy = y + padIn + i * rowH + rowH / 2;
    const mx = x + padIn + 8;
    const marker = it.supra
      ? `<path d="M${mx},${cy - 6} L${mx + 6},${cy} L${mx},${cy + 6} L${mx - 6},${cy} Z" fill="${it.color}" stroke="${COLOR_SUPRA_STROKE}" stroke-width="1.5" />`
      : `<circle cx="${mx}" cy="${cy}" r="5" fill="${it.color}" stroke="#ffffff" stroke-width="1" />`;
    rows += `${marker}<text x="${x + padIn + 22}" y="${cy + 4}" font-family="'JetBrains Mono', monospace" font-size="12" fill="#263238">${it.label}</text>`;
  });
  return `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="6" fill="#ffffff" fill-opacity="0.92" stroke="#cfd8dc" stroke-width="1" />${rows}`;
}

/**
 * Construye el mapa esquemático del municipio como data-URL SVG.
 *
 * @param points   Registros con coordenadas UTM30 (`xUtm30`/`yUtm30`), confianza y flags.
 * @param boundary Límite municipal (WGS84) o `null` → esquema solo-puntos (DOP-VACÍO α).
 * @param options  Tamaño lógico y padding del SVG.
 * @returns        `data:image/svg+xml,<URI>` o `''` si no hay puntos válidos (INF-5b lo omite).
 */
export function buildSchematicMapDataUrl(
  points: ReadonlyArray<SchematicMapPoint>,
  boundary: MunicipalBoundary | null,
  options: SchematicMapOptions = {},
): string {
  const { width, height, padding } = { ...DEFAULTS, ...options };

  const plotted = extractPoints(points);
  if (plotted.length === 0) return '';

  const rings = projectBoundary(boundary);
  const union: XY[] = [...plotted, ...rings.flat()];
  const bbox = unionBBox(union);
  const proj = makeProjector(bbox, width, height, padding);

  const boundaryStr = boundarySvg(rings, proj);
  // Supra al final (encima) para que su marcador no quede oculto por círculos.
  const ordered = [...plotted].sort((a, b) => Number(a.supra) - Number(b.supra));
  const pointsStr = ordered.map((p) => pointSvg(p, proj)).join('');
  const legendStr = legendSvg();

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
    `width="${width}" height="${height}" role="img">` +
    `<rect x="0" y="0" width="${width}" height="${height}" fill="${COLOR_BG}" />` +
    boundaryStr +
    pointsStr +
    legendStr +
    `</svg>`;

  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export default buildSchematicMapDataUrl;
